import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { mentorshipApi, paymentApi, showError, showSuccess } from "../lib";
import {
  CheckCircle,
  Loader2,
  Video,
  Calendar,
  Clock,
  MapPin,
  ArrowRight,
  Award,
} from "lucide-react";

const MentorshipPaymentSuccessPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"loading" | "success" | "failed">(
    "loading",
  );
  const [session, setSession] = useState<any>(null);
  const [paymentDetails, setPaymentDetails] = useState<any>(null);

  useEffect(() => {
    const verifyPayment = async () => {
      const searchParams = new URLSearchParams(location.search);
      const transactionId =
        searchParams.get("transactionId") || searchParams.get("transId");
      const sessionId = searchParams.get("sessionId");

      if (!transactionId) {
        setStatus("failed");
        showError("Missing transaction ID");
        return;
      }

      let attempts = 0;
      const maxAttempts = 20;
      const pollInterval = 2500;

      while (attempts < maxAttempts) {
        try {
          // Check payment status using paymentApi
          const statusRes =
            await paymentApi.checkMentorshipPaymentStatus(transactionId);

          if (statusRes.success && statusRes.data?.status === "completed") {
            setStatus("success");
            setPaymentDetails(statusRes.data);

            // Get session details if sessionId is available
            if (sessionId || statusRes.data?.session) {
              try {
                const sessionDetails = await mentorshipApi.getSessionDetails(
                  sessionId || statusRes.data.session,
                );
                if (sessionDetails.success) {
                  setSession(sessionDetails.data);
                }
              } catch (err) {
                console.error("Failed to fetch session details:", err);
              }
            }
            showSuccess("Payment verified successfully!");
            return;
          }
        } catch (err) {
          console.log(
            "Payment verification attempt",
            attempts + 1,
            "failed:",
            err,
          );
        }

        attempts++;
        if (attempts < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, pollInterval));
        }
      }

      setStatus("failed");
      showError("Payment verification timed out. Please contact support.");
    };

    verifyPayment();
  }, [location.search]);

  useEffect(() => {
    if (status === "success") {
      const timer = setTimeout(() => {
        navigate("/dashboard/student/mentorship-sessions", { replace: true });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [status, navigate]);

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      }),
      time: date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      }),
    };
  };

  if (status === "loading") {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50/30 px-4">
        <div className="text-center">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-orange-400/20 animate-ping"></div>
            <Loader2 className="w-16 h-16 mx-auto mb-4 text-orange-500 animate-spin relative z-10" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Verifying Payment...
          </h2>
          <p className="text-sm text-gray-600">
            Please wait while we confirm your payment status.
          </p>
          <p className="text-xs text-gray-400 mt-2">
            This may take a few seconds
          </p>
        </div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50/30 px-4 py-8">
        <div className="w-full max-w-md p-6 text-center bg-white rounded-2xl shadow-xl border border-gray-100">
          <div className="w-20 h-20 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg
              className="w-10 h-10 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h2 className="mb-2 text-2xl font-bold text-gray-900">
            Payment Failed
          </h2>
          <p className="mb-6 text-sm text-gray-600">
            Your payment could not be processed. Please try again or contact
            support.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <button
              onClick={() => navigate("/mentorship")}
              className="px-6 py-2.5 text-sm font-medium text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate("/dashboard/student/mentorship-sessions")}
              className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all"
            >
              My Sessions
            </button>
          </div>
        </div>
      </div>
    );
  }

  const sessionDateTime = session?.scheduledAt
    ? formatDateTime(session.scheduledAt)
    : null;

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50/30 px-4 py-8">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        {/* Success Header */}
        <div className="relative bg-gradient-to-r from-emerald-600 to-green-500 py-8">
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage:
                "radial-gradient(circle at 30% 60%, rgba(255,255,255,0.4) 1px, transparent 1px), radial-gradient(circle at 70% 30%, rgba(255,255,255,0.3) 1px, transparent 1px)",
              backgroundSize: "20px 20px, 30px 30px",
            }}
          />
          <div className="relative text-center">
            <div className="w-20 h-20 mx-auto mb-3 bg-white/20 rounded-full flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">
              Payment Successful!
            </h1>
            <p className="text-emerald-100 text-sm mt-1">
              Your mentorship session is confirmed
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Session Details */}
          {session && (
            <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-100">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                Session Details
              </h3>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-900">
                  {session.topic || "Mentorship Session"}
                </p>
                {sessionDateTime && (
                  <>
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>{sessionDateTime.date}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <Clock className="w-3.5 h-3.5" />
                      <span>
                        {sessionDateTime.time} • {session.duration || 60}{" "}
                        minutes
                      </span>
                    </div>
                  </>
                )}
                {session.meetingLink && (
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <a
                      href={session.meetingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-orange-600 hover:text-orange-700"
                    >
                      <Video className="w-3 h-3" />
                      Join Meeting Link
                      <ArrowRight className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Payment Info */}
          {paymentDetails && (
            <div className="mb-6 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Payment Information
              </h3>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Amount Paid</span>
                <span className="text-lg font-bold text-gray-900">
                  {paymentDetails.amount
                    ? new Intl.NumberFormat("fr-CM", {
                        style: "currency",
                        currency: "XAF",
                        maximumFractionDigits: 0,
                      }).format(paymentDetails.amount)
                    : "Processing"}
                </span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-xs text-gray-500">Transaction ID</span>
                <span className="text-xs font-mono text-gray-600">
                  {paymentDetails.transactionId?.slice(-12) || "Pending"}
                </span>
              </div>
            </div>
          )}

          {/* What's Next */}
          <div className="mb-6 p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl border border-orange-100">
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-4 h-4 text-orange-500" />
              <h3 className="text-sm font-semibold text-gray-900">
                What's Next?
              </h3>
            </div>
            <ul className="space-y-2 text-xs text-gray-600">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
                <span>Your session is confirmed with the mentor</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
                <span>A meeting link will be available before the session</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
                <span>You can view all your sessions in the dashboard</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
                <span>A confirmation email has been sent to your inbox</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            <button
              onClick={() => navigate("/dashboard/student/mentorship-sessions")}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 text-sm font-medium text-white rounded-xl bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-lg transition-all duration-300"
            >
              <Video className="w-4 h-4" />
              View My Sessions
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate("/mentorship")}
              className="w-full px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all"
            >
              Browse More Mentors
            </button>
          </div>

          {/* Auto Redirect Note */}
          <p className="mt-4 text-center text-[10px] text-gray-400">
            Redirecting to your sessions in a few seconds...
          </p>
        </div>
      </div>
    </div>
  );
};

export default MentorshipPaymentSuccessPage;
